<template>
  <div
    :id="id"
    ref="containerElement"
    class="net__container"
    :style="{
      '--net-color-background': sceneTree.activeScene?.backgroundColor || 'var(--color-background-deep)',
    }"
    :class="{
      '-panning': state.activeMouseAction == 'pan'
    }"
    @click="$emit('click', $event)"
  >
    <div
      ref="canvasElement"
      class="net-canvas"
      :style="{
        backgroundImage: `url(${backgroundImageData})`,
        backgroundSize: backgroundImage?.objectFit == 'none' ? 'auto' : backgroundImage?.objectFit,
        backgroundRepeat: backgroundImage?.repeat
      }"
      @wheel="onMouseWheel"
      @contextmenu="onContextMenu"
      @pointerdown="onMouseDown"
      @touchstart="onTouchStart"
      @touchmove="onTouchMove"
      @touchend="onTouchEnd"
      @touchcancel="onTouchEnd"
    >
      <div ref="canvasContentElement" class="net-canvas-content">
        <div
          v-for="layerTreeNode in sceneTree.layerNodes"
          :key="layerTreeNode.uid"
          class="net-canvas__layer"
          :style="{
            transform: state.cachedCanvasTransform
          }"
        >
          <template v-if="layerTreeNode.node.data.layerType === 'scene'">
            <template v-for="childNode in layerTreeNode.children" :key="childNode.uid">
              <scene-node
                :tree-node="childNode"
                :item-sizes="state.itemSizes"
                @select-item="select($event)"
                @add-select-item="addToSelection($event)"
                @update:element-value="emit('update:element-value', $event)"
                @toggle-relation-mode="emit('toggle-relation-mode', $event)"
                @toggle-flow-mode="emit('toggle-flow-mode', $event)"
                @update:tool="emit('update:tool', $event)"
                @contextmenu="onContextMenu"
                @wheel="onMouseWheel"
              />
            </template>
          </template>

          <SceneLinksLayer
            v-else-if="layerTreeNode.node.data.layerType == 'links' && layerTreeNode.visible"
            :node="layerTreeNode"
            :item-sizes="state.itemSizes"
            :last-element-size-change="state.lastElementSizeChange"
            :element-node-at-mouse="elementNodeAtMouse"
            @select-item="select($event)"
            @add-select-item="addToSelection($event)"
            @update:element-value="emit('update:element-value', $event)"
            @toggle-relation-mode="emit('toggle-relation-mode', $event)"
            @toggle-flow-mode="emit('toggle-flow-mode', $event)"
            @update:tool="emit('update:tool', $event)"
            @contextmenu="onContextMenu"
            @wheel="onMouseWheel"
          />
        </div>

        <div
          class="net-canvas__layer net-canvas__ui-layer"
          :style="{
            transform: state.cachedCanvasTransform
          }"
        >
          <net-question
            :editable="mode == 'edit'"
            @select="select($event)"
            @contextmenu="onContextMenu"
          />

          <div
            v-show="selectionBox.visible"
            class="net__selectionbox"
            :style="{
              left: `${selectionBoxDimensions.left}px`,
              top: `${selectionBoxDimensions.top}px`,
              width: `${selectionBoxDimensions.width}px`,
              height: `${selectionBoxDimensions.height}px`
            }"
          ></div>
          <div
            v-if="creationBoxDimensions.visible"
            class="net__creationbox"
            :style="{
              left: `${creationBoxDimensions.left}px`,
              top: `${creationBoxDimensions.top}px`,
              width: `${creationBoxDimensions.width}px`,
              height: `${creationBoxDimensions.height}px`
            }"
            :class="{
              '-transparent': creationBox.placeholderComponent != null
            }"
          >
            <component
              v-if="creationBox.placeholderComponent"
              :is="creationBox.placeholderComponent"
              :size="{
                width: creationBoxDimensions.width,
                height: creationBoxDimensions.height
              }"
            />
          </div>

          <net-transform-frame
            v-if="mode === 'edit' && tool == 'pointer'"
            :selection="selection"
            :item-sizes="state.itemSizes"
            :blocked="touchGesturing"
            @resize-end="updateSelectedElementSizes"
            @move-end="updateSelectedElementSizes"
            @move="onItemsMove"
            @resize="onItemsResize"
            @copy="emit('copy', $event)"
            @select="selectMany"
            @contextmenu="onContextMenu"
          ></net-transform-frame>
          <net-dynamic-action-bar
            v-if="mode == 'edit'"
            :selection="selection"
            :item-sizes="state.itemSizes"
            :tool="tool"
            :flow-source-element="flowSourceElement"
            :impact-source-element="impactSourceElement"
            @toggle-relation-mode="emit('toggle-relation-mode', $event)"
            @toggle-flow-mode="emit('toggle-flow-mode', $event)"
            @select="select($event)"
            @update:tool="emit('update:tool', $event)"
            @wheel="onMouseWheel"
          />
          <net-dynamic-texttoolbar
            v-if="mode == 'edit'"
            :selection="selection"
            :item-sizes="state.itemSizes"
            :tool="tool"
            @update:tool="emit('update:tool', $event)"
            @wheel="onMouseWheel"
          />
        </div>
      </div>

      <slot name="canvas"></slot>
    </div>

    <index-list-over-net
      v-if="foregroundIndexNodes.length > 0"
      :foreground-index-nodes="foregroundIndexNodes"
      :selection="selection"
      :element-values="elementValues"
      @select="select"
      @contextmenu="onContextMenu"
    />

    <net-footer
      class="net__footer"
      :net-mode="mode"
      v-model:scale="transform.scale"
      @update:scale="emit('update:net-transform', { scale: $event })"
      @request-fullscreen="requestFullscreen()"
    >
      <elements-foreground-list
        class="p-2"
        :element-nodes="foregroundElementNodes"
        :selected-nodes="selectedSceneNodes"
        :impulses="impulses"
        :open-effects="openEffects"
        :disabled-uids="props.disabledItemUids"
        :costs="elementCosts"
        :element-values="elementValues"
        :selection="selection"
        @select="select"
        @contextmenu="onContextMenu($event)"
      />
    </net-footer>
    <slot></slot>
  </div>
</template>

<script setup lang="ts">
import { computed, provide, reactive, ref, type Component } from "vue";
import { watch, onMounted, onBeforeUnmount } from "vue";
import { useMagicKeys, useMouse } from "@vueuse/core";
import { filter, map } from "lodash-es";
import { v4 } from "uuid";

import NetQuestion from "@editor/items/question/NetQuestion.vue";
import NetFooter from "@editor/net/NetFooter.vue";
import ElementsForegroundList from "@editor/items/resource/ElementsForegroundList.vue";
import NetTransformFrame from "@editor/net/NetTransformFrame.vue";
import NetDynamicActionBar from "@editor/net/NetDynamicActionBar.vue";
import NetDynamicTexttoolbar from "@editor/net/NetTexttoolbar.vue";
import SceneNode from "@editor/items/scene/SceneNode.vue";
import SceneLinksLayer from "@editor/items/sceneLayer/SceneLinksLayer.vue";

import IndexListOverNet from "@editor/items/index/IndexListOverNet.vue";

import { useAreaStore } from "@editor/items/area/areas.store";
import { useNetCoords } from "./netCoordsModifier";
import { useNetDropHandler } from "./netDropHandler";
import { useImageStore } from "@editor/items/image/images.store";
import { useSceneTree } from "@editor/items/scene/sceneTree";

import { getFlowNetItems } from "@editor/items/flow/flowFunctions";

import type {
  ElementPlaceholderItem,
  TargetItem
} from "@editor/items/target/TargetItem";
import type { ResourceItem } from "@editor/items/resource/ResourceItem";
import type { Handleable } from "@core/types/Events";
import type { AreaItem } from "@editor/items/area/AreaItem";
import type { IndexItem } from "@editor/items/index/IndexItem";
import type { FlowItem } from "@editor/items/flow/FlowItem";
import type { FlowBranchItem } from "@editor/items/flow/FlowBranchItem";
import type { MomentumItem } from "@editor/items/momentum/MomentumItem";
import type { RelationItem } from "@editor/items/relation/RelationItem";
import type { QuestionItem } from "@editor/items/question/QuestionItem";
import type {
  RawNetCanvasProps,
  NetEmits,
  NetSelectableItemType,
  ContextMouseEvent
} from "./NetCanvasProps";
import type { Uid } from "@/helpers/utility/Id";
import type { SceneNodeType } from "@editor/items/scene/SceneNodeData";
import type { SceneTreeItemSize, SceneTreeNode } from "@editor/items/scene/SceneTreeItem";

import {
  keyCanvasDimensions,
  keyNetItems,
  keyNetScale,
  keyItemSizes,
  keyRoundIndex,
  keyTargets,
  keyIndices,
  keyResources,
  keyNetId,
  keyElementValues,
  keyNetCanvas,
} from "./netCanvasInjectionKeys";
import { isUidOfType, splitUid } from "@/helpers/utility/Id";
import { notNull } from "@/helpers/utility/Type";
import { netTools } from "./NetTools";
import { doBoxesOverlap } from "@/helpers/math/geometry";
import { isLayerTreeNode } from "@editor/items/sceneLayer/sceneLayerFunctions";
import { isGroupTreeNode } from "@editor/items/sceneGroup/sceneGroupFunctions";
import { isElementTreeNode } from "@editor/items/sceneElement/sceneElementFunctions";
import { getAllDescendantUids } from "@editor/items/scene/sceneNodeFunctions";
import { useSceneFlowNodeStore } from "@editor/items/flow/sceneFlowNodes";

type ElementItem = TargetItem | ResourceItem | IndexItem;
type ContextItem =
  | ElementItem
  | FlowBranchItem
  | RelationItem
  | QuestionItem
  | ElementPlaceholderItem
  | MomentumItem
  | FlowItem
  | AreaItem;

const props = withDefaults(defineProps<RawNetCanvasProps>(), {
  id: () => `net-${v4()}`,

  mode: "view",
  tool: "pan",

  netTransform: () => ({
    translateX: 0,
    translateY: 0,
    scale: 1
  }),

  impulses: () => ({
    target: {},
    resource: {}
  }),
  openEffects: () => ({
    target: {},
    resource: {}
  }),
  elementCosts: () => ({}),

  effectValues: () => ({}),

  fadeRelations: false,
  fadeElements: false,
  showSelectedNetItemConnections: true,

  disabledItemUids: () => new Set<Uid<"resource" | "target" | "index" | "sceneNode">>(),
  fixedItem: undefined,
  impactSourceElement: undefined,
  flowSourceElement: undefined,

  roundIndex: 0
});

const emit = defineEmits<NetEmits>();

const canvasElement = ref<HTMLElement>();
const canvasContentElement = ref<HTMLElement>();

const { shift } = useMagicKeys();

const GRID_SIZE = 10;

const state = reactive({
  // transformation
  activeMouseAction: null as "pan" | "select" | "create" | null,
  spaceDown: false,

  itemSizes: {} as {
    [uid: Uid<"sceneNode" | "flow">]: SceneTreeItemSize;
  },
  lastElementSizeChange: 0,

  cachedCanvasTransform: "",
  isUpdatingCachedCanvasTransform: false,
  cachedCanvasTransformUpdatePending: false
});

const transform = reactive({
  translateX: 0,
  translateY: 0,
  scale: 1
});

// Provide the canvas state for nested objects.
provide(
  keyNetItems,
  computed(() => ({
    flow: props.flows,
    target: props.targets,
    resource: props.resources,
    index: props.indices
  }))
);
provide(
  keyTargets,
  computed(() => props.targets)
);
provide(
  keyResources,
  computed(() => props.resources)
);
provide(
  keyIndices,
  computed(() => props.indices)
);
provide(
  keyItemSizes,
  computed(() => state.itemSizes)
);
provide(
  keyNetScale,
  computed(() => transform.scale)
);
provide(
  keyElementValues,
  computed(() => props.elementValues)
);
provide(
  keyRoundIndex,
  computed(() => props.roundIndex)
);
provide(
  keyNetId,
  computed(() => props.id)
);

provide(
  keyNetCanvas,
  computed(() => ({
    props,
    emit,
    transform
  }))
);

const sceneTree = useSceneTree();

let onResize = () => {};
let resizeObserver: ResizeObserver | null = null;

const areas = computed(() => {
  return useAreaStore().orderedItems;
});
const sceneFontSize = computed(() => {
  return sceneTree.activeScene?.fontSize ?? 16;
});

const selectionBox = reactive({
  visible: false,
  x: 0,
  y: 0,
  width: 0,
  height: 0
});
const creationBox = reactive({
  x: 0,
  y: 0,
  width: 0,
  height: 0,
  placeholderComponent: undefined as Component | undefined
});

const selectionBoxDimensions = computed(() => {
  let left, top, width, height;
  if (selectionBox.width < 0) {
    width = -selectionBox.width;
    left = selectionBox.x - width;
  } else {
    left = selectionBox.x;
    width = selectionBox.width;
  }
  if (selectionBox.height < 0) {
    height = -selectionBox.height;
    top = selectionBox.y - height;
  } else {
    top = selectionBox.y;
    height = selectionBox.height;
  }
  return {
    visible: width > 4 && height > 4,
    left,
    top,
    width,
    height
  };
});
const creationBoxDimensions = computed(() => {
  let left, top, width, height;
  const size = Math.max(
    Math.abs(creationBox.width),
    Math.abs(creationBox.height)
  );

  if (creationBox.width < 0) {
    if (shift.value) {
      width = size;
    } else {
      width = -creationBox.width;
    }
    left = creationBox.x - width;
  } else {
    if (shift.value) {
      width = size;
    } else {
      width = creationBox.width;
    }
    left = creationBox.x;
  }
  if (creationBox.height < 0) {
    if (shift.value) {
      height = size;
    } else {
      height = -creationBox.height;
    }
    top = creationBox.y - height;
  } else {
    if (shift.value) {
      height = size;
    } else {
      height = creationBox.height;
    }
    top = creationBox.y;
  }

  const visible = width > 4 && height > 4;

  return {
    visible,
    left,
    top,
    width: visible ? width : 0,
    height: visible ? height : 0
  };
});

const visiblePlane = computed(() => {
  return {
    width: 1000 / transform.scale,
    height: 700 / transform.scale
  };
});

const selectedSceneNodes = computed(() => {
  const sceneNodeUids = [...props.selection.selected].filter(isUidOfType("sceneNode"));
  return sceneNodeUids.map((uid) => sceneTree.treeNodes.get(uid)).filter(notNull);
});

// ================================
// FOREGROUND

const foregroundLayer = computed(() => {
  return sceneTree.layerNodes.find((layerTreeNode) => layerTreeNode.node.data.layerType === "foreground");
});

const foregroundItemNodes = computed(() => {
  if (!foregroundLayer.value) return [];

  const descendantUids = getAllDescendantUids(foregroundLayer.value.node);
  const descendantNodes = [...descendantUids]
    .map((uid) => sceneTree.treeNodes.get(uid))
    .filter(notNull)
    .filter((treeNode) => treeNode.visible)
    .filter((treeNode) => isElementTreeNode(treeNode));
  return descendantNodes;
});

const foregroundElementNodes = computed(() => {
  if (!foregroundLayer.value) return [];

  const elementNodes = foregroundItemNodes.value
    .map((treeNode) => {
      const { type } = splitUid(treeNode.node.data.elementUid);
      if (type === 'index') return null;
      return treeNode;
    })
    .filter(notNull);
  
  elementNodes.sort((a, b) => {
    return b.order - a.order;
  });
  
  return elementNodes;
});

const foregroundIndexNodes = computed(() => {
  if (!foregroundLayer.value) return [];

  const indexNodes = foregroundLayer.value.children
    .filter((treeNode) => treeNode.visible)
    .filter((treeNode) => isElementTreeNode(treeNode))
    .map((treeNode) => {
      const { type } = splitUid(treeNode.node.data.elementUid);
      if (type !== "index") return null;
      return treeNode;
    })
    .filter(notNull);
  
  indexNodes.sort((a, b) => {
    return b.order - a.order;
  });

  return indexNodes;
});

const netTool = computed(() => {
  return netTools[props.tool];
});

const imageStore = useImageStore();
const backgroundImage = computed(() => {
  if (sceneTree.activeScene.backgroundImageId == null) return null;
  return imageStore.items[sceneTree.activeScene.backgroundImageId];
});
const backgroundImageData = computed(() => {
  if (sceneTree.activeScene.backgroundImageId == null) return null;
  return imageStore.mediaData[sceneTree.activeScene.backgroundImageId];
});

onMounted(() => {
  window.addEventListener("keydown", onWindowKeyDown);
  window.addEventListener("keyup", onWindowKeyUp);

  updateDimensionVars();

  setTimeout(() => {
    // Safari needs a little time to get the correct dimensions.
    if (canvasContentElement.value) {
      canvasContentElement.value.style.display = "flex";
    }
  }, 2);

  if (ResizeObserver && canvasElement.value) {
    resizeObserver = new ResizeObserver(() => {
      updateDimensionVars();
    });
    resizeObserver.observe(canvasElement.value);
  } else {
    onResize = () => updateDimensionVars();
  }

  window.addEventListener("resize", onResize);
});

onBeforeUnmount(() => {
  window.removeEventListener("keydown", onWindowKeyDown);
  window.removeEventListener("keyup", onWindowKeyUp);
  window.removeEventListener("resize", onResize);

  if (resizeObserver) {
    resizeObserver.disconnect();
  }
});

function select(data: { uid: Uid<NetSelectableItemType> }) {
  console.log("select item", data);
  emit("select-item", data, getItemPath(data.uid));
}
function addToSelection(data: { uid: Uid<NetSelectableItemType> }) {
  emit("add-select-item", data, getItemPath(data.uid));
}
function deselectAnything() {
  emit("clear-all-selection");
}
function selectMany(items: { uid: Uid<NetSelectableItemType>; }[]) {
  emit("select-many", items);
}

/**
 * Handle keydown events on the window.
 */
function onWindowKeyDown(keyEvent: KeyboardEvent & Handleable) {
  let prevent = false;

  if (keyEvent.hasBeenHandled) return;
  if (keyEvent.defaultPrevented) return;

  if (keyEvent.key === " " || keyEvent.key === "Spacebar") {
    state.spaceDown = true;
  }

  const ctrlPressed = keyEvent.ctrlKey || keyEvent.metaKey;
  const altPressed = keyEvent.altKey;

  // zoom in (ctrl+"+")
  if (keyEvent.key === "+" && ctrlPressed) {
    zoomIn();
    prevent = true;
  }
  // zoom out (ctrl+"-")
  if (keyEvent.key === "-" && ctrlPressed) {
    zoomOut();
    prevent = true;
  }

  // reset zoom & panning
  if (keyEvent.key === "0" && ctrlPressed) {
    transform.translateX = 0;
    transform.translateY = 0;
    transform.scale = 1;

    emit("update:net-transform", {
      translateX: 0,
      translateY: 0,
      scale: 1
    });
    state.activeMouseAction = null;
    prevent = true;
  }

  // Adjust the view to show all elements inside
  if (ctrlPressed && altPressed && keyEvent.key === "1") {
    adjustView();
    prevent = true;
  }

  if (keyEvent.key === "Backspace" || keyEvent.key === "Delete") {
    const target = keyEvent.target;
    if (!(target instanceof HTMLElement)) return;

    const isEditable = target.isContentEditable;
    if (isEditable) return;

    // check if the current target is the body OR an element in the canvas
    const isBodyElement = target.tagName === "BODY";
    const isCanvasElement = target.closest(".net-canvas") !== null;

    // Prevent navigating back via backspace
    // (this interferes with deleting of net components)
    if (isBodyElement || isCanvasElement) {
      prevent = true;

      if (props.mode == "edit") {
        emit("delete");
      }
    }
  }

  if (prevent) {
    keyEvent.preventDefault();
    keyEvent.stopPropagation();
  }
}

/**
 * @param keyEvent
 */
function onWindowKeyUp(keyEvent: KeyboardEvent & Handleable) {
  if (keyEvent.key === " " || keyEvent.key === "Spacebar") {
    state.spaceDown = false;
  }

  if (keyEvent.hasBeenHandled) return;
  if (keyEvent.defaultPrevented) return;
  if (keyEvent.target !== document.body) return;
}

function onMouseWheel(event: WheelEvent) {
  if (event.ctrlKey || event.metaKey) {
    onMouseWheelZoom(event);
  } else {
    onMouseWheelPan(event);
  }
}

/**
 * Handle zooming by mouse wheel
 */
function onMouseWheelZoom(event: WheelEvent) {
  let deltaY = event.deltaY;
  if (Math.abs(deltaY) < 100) deltaY *= 10;
  if (deltaY < 0) {
    zoomIn(Math.abs(deltaY));
  } else {
    zoomOut(Math.abs(deltaY));
  }
  event.preventDefault();
}

/**
 * Handle panning by mouse wheel
 */
function onMouseWheelPan(event: WheelEvent) {
  if (event.shiftKey) {
    transform.translateX -= event.deltaY;
    transform.translateY -= event.deltaX;
  } else {
    transform.translateX -= event.deltaX;
    transform.translateY -= event.deltaY;
  }
  emit("update:net-transform", {
    translateX: transform.translateX,
    translateY: transform.translateY
  });
  event.preventDefault();
}

// ===============================
// ZOOMING AND VIEW ADJUSTMENT

const MIN_ZOOM_SCALE = 0.1;
const MAX_ZOOM_SCALE = 2;

function zoomOut(delta = 100) {
  const nextScaleStep = (transform.scale / 10) * 9;
  const difference = nextScaleStep - transform.scale;
  transform.scale += difference * (delta / 100);

  const minScale = MIN_ZOOM_SCALE;
  if (transform.scale < minScale) transform.scale = minScale;

  emit("update:net-transform", {
    scale: transform.scale
  });
}

function zoomIn(delta = 100) {
  const nextScaleStep = (transform.scale / 9) * 10;
  const difference = nextScaleStep - transform.scale;
  transform.scale += difference * (delta / 100);

  const maxScale = MAX_ZOOM_SCALE;
  if (transform.scale > maxScale) transform.scale = maxScale;

  emit("update:net-transform", {
    scale: transform.scale
  });
}

/**
 * scale and transform the view until all elements fit in.
 */
function adjustView() {
  if (areas.value.length == 0 && sceneTree.treeNodes.size == 0) {
    transform.translateX = 0;
    transform.translateY = 0;
    transform.scale = 1;
    emit("update:net-transform", {
      translateX: transform.translateX,
      translateY: transform.translateY,
      scale: transform.scale
    });
    return;
  }

  const bounds = {
    left: Infinity,
    top: Infinity,
    right: -Infinity,
    bottom: -Infinity,
    width: 0,
    height: 0
  };
  for (const area of areas.value) {
    if (area.x < bounds.left) bounds.left = area.x;
    if (area.x + area.width > bounds.right) {
      bounds.right = area.x + area.width;
    }
    if (area.y < bounds.top) bounds.top = area.y;
    if (area.y + area.height > bounds.bottom) {
      bounds.bottom = area.y + area.height;
    }
  }
  for (const node of sceneTree.treeNodes.values()) {
    const itemSize = state.itemSizes[node.uid];
    if (!itemSize) continue;
    if (itemSize.left < bounds.left) bounds.left = itemSize.left;
    if (itemSize.right > bounds.right) bounds.right = itemSize.right;
    if (itemSize.top < bounds.top) bounds.top = itemSize.top;
    if (itemSize.bottom > bounds.bottom) bounds.bottom = itemSize.bottom;
  }
  bounds.width = bounds.right - bounds.left;
  bounds.height = bounds.bottom - bounds.top;

  // center pan on visible content
  transform.translateX = -(bounds.width / 2 - 500 + bounds.left);
  transform.translateY = -(bounds.height / 2 - 350 + bounds.top);

  // zoom until everything can be viewed
  const minScale = MIN_ZOOM_SCALE;
  while (
    transform.scale > minScale &&
    (bounds.width > visiblePlane.value.width ||
      bounds.height > visiblePlane.value.height)
  ) {
    zoomOut();
  }

  emit("update:net-transform", {
    translateX: transform.translateX,
    translateY: transform.translateY
  });
}

const touchesCache: {
  identifier: number;
  x: number;
  y: number;
}[] = [];
const startTouchDistance = ref(0);
const lastTouchDistance = ref(0);
const lastTouchCenter = ref({ x: 0, y: 0 });
const touchGesturing = ref(false);

function onTouchStart(touchEvent: TouchEvent & Handleable) {
  if (touchEvent.hasBeenHandled) return;

  const touches = touchEvent.touches;

  // store the current touches
  touchesCache.length = 0;
  for (const touch of touches) {
    touchesCache.push({
      identifier: touch.identifier,
      x: touch.clientX,
      y: touch.clientY
    });
  }

  if (touchesCache.length >= 2) {
    touchEvent.hasBeenHandled = true;
    touchGesturing.value = true;

    const [touch1, touch2] = touchesCache;

    // start zooming
    const distance = Math.hypot(touch1.x - touch2.x, touch1.y - touch2.y);
    lastTouchDistance.value = distance;
    startTouchDistance.value = distance;

    // prepare touch panning
    lastTouchCenter.value = {
      x: (touch1.x + touch2.x) / 2,
      y: (touch1.y + touch2.y) / 2
    };

    touchEvent.preventDefault();
  }
}

function onTouchMove(touchEvent: TouchEvent & Handleable) {
  if (touchEvent.hasBeenHandled) return;
  touchEvent.hasBeenHandled = true;

  // update the current touches
  for (const touch of touchEvent.touches) {
    const cachedTouch = touchesCache.find(
      (t) => t.identifier === touch.identifier
    );
    if (cachedTouch) {
      cachedTouch.x = touch.clientX;
      cachedTouch.y = touch.clientY;
    } else {
      // if the touch is not in the cache, we add it
      touchesCache.push({
        identifier: touch.identifier,
        x: touch.clientX,
        y: touch.clientY
      });
    }
  }

  if (touchesCache.length >= 2) {
    touchGesturing.value = true;
    touchEvent.preventDefault();

    // calculate the new distance between the two touches
    const [touch1, touch2] = touchesCache;

    if (!touch1 || !touch2) {
      // if we don't have two touches, we don't handle the touch event
      return;
    }

    const distance = Math.hypot(touch1.x - touch2.x, touch1.y - touch2.y);

    // zoom in or out depending on the distance change
    const deltaStartDistance = distance - startTouchDistance.value;
    const deltaDistance = distance - lastTouchDistance.value;
    if (Math.abs(deltaStartDistance) > 20) {
      // only zoom if the distance has changed significantly

      // calculate the zoom factor based on the distance change
      const zoomFactor = deltaDistance / 300; // adjust the divisor to control zoom speed
      transform.scale += zoomFactor;
      const minScale = MIN_ZOOM_SCALE;
      const maxScale = MAX_ZOOM_SCALE;
      if (transform.scale < minScale) transform.scale = minScale;
      if (transform.scale > maxScale) transform.scale = maxScale;
    }

    lastTouchDistance.value = distance;

    // calculate the new center point of the two touches
    const newCenter = {
      x: (touch1.x + touch2.x) / 2,
      y: (touch1.y + touch2.y) / 2
    };
    // calculate the translation needed to keep the center point in the same place
    const deltaX = (newCenter.x - lastTouchCenter.value.x) / transform.scale;
    const deltaY = (newCenter.y - lastTouchCenter.value.y) / transform.scale;
    transform.translateX += deltaX;
    transform.translateY += deltaY;

    lastTouchCenter.value = newCenter;

    emit("update:net-transform", {
      scale: transform.scale,
      translateX: transform.translateX,
      translateY: transform.translateY
    });
  }
}

function onTouchEnd(touchEvent: TouchEvent & Handleable) {
  touchGesturing.value = false;
  touchEvent.hasBeenHandled = true;

  // reset the touches cache
  touchesCache.length = 0;

  // reset the last touch distance and center
  lastTouchDistance.value = 0;
  lastTouchCenter.value = { x: 0, y: 0 };
}

function onContextMenu(
  mouseEvent: MouseEvent &
    Handleable & {
      item?: { uid: Uid<ContextItem["type"]> } | null;
      items?: { uid: Uid<"relation" | "sceneNode" | "flow" | "flowBranch"> }[] | null;
      mousePosition?: { x: number; y: number };
      netPosition?: { x: number; y: number };
      path?: Uid<"sceneNode">[];
    },
  item?: { uid: Uid<ContextItem["type"]> } | null
) {
  if (mouseEvent.hasBeenHandled) return;
  mouseEvent.hasBeenHandled = true;
  mouseEvent.preventDefault();

  const mousePosition = {
    x: mouseEvent.x - 2,
    y: mouseEvent.y - 2
  };
  const netPosition = toLocal(mousePosition);

  if (item) mouseEvent.item = item;
  mouseEvent.mousePosition = mousePosition;
  mouseEvent.netPosition = netPosition;

  const eventPayload = mouseEvent as ContextMouseEvent;

  emit("contextmenu", eventPayload);
}

function getItemPath(uid: Uid<string> | undefined): Uid<"sceneNode">[] {
  const path: Uid<"sceneNode">[] = [];

  if (!uid) return path;
  if (!isUidOfType(uid, "sceneNode")) return path;

  let current: SceneTreeNode | undefined = sceneTree.treeNodes.get(uid);
  while (current) {
    path.unshift(current.uid);
    if (current.parentUid) {
      current = sceneTree.treeNodes.get(current.parentUid);
    } else {
      break;
    }
  }

  return path;
}

// ===============================
// MOUSE ACTIONS

function onMouseDown(mouseEvent: MouseEvent & Handleable) {
  if (mouseEvent.hasBeenHandled) return;
  if (mouseEvent.defaultPrevented) return;
  if (touchGesturing.value) return; // ignore mouse events while touching

  const MAIN_MOUSE_BUTTON = 0;
  const MIDDLE_MOUSE_BUTTON = 1;
  const RIGHT_MOUSE_BUTTON = 2;

  if (mouseEvent.button == RIGHT_MOUSE_BUTTON) {
    return;
  }

  let toolAction = netTool.value.type;

  if (mouseEvent.button == MIDDLE_MOUSE_BUTTON) {
    toolAction = "dragging";
  }

  if (
    (mouseEvent.button === MAIN_MOUSE_BUTTON && toolAction == "selecting") ||
    (toolAction == "dragging" && mouseEvent.shiftKey)
  ) {
    onSelectStart(mouseEvent);
  }

  // start panning:
  else if (toolAction == "dragging") {
    onPanStart(mouseEvent);
  }

  // start creating a new item:
  else if (toolAction == "creating") {
    onCreateStart(mouseEvent);
  }

  // Special tools are not handled by the net canvas.
  else if (toolAction == "special") {
    emit("update:tool", "pointer");
    return;
  }
}

// ===============================
// MOUSE ACTION: PANNING

/**
 * Last pan position, in local coordinates.
 * Set at the start of a pan action.
 */
const startPanPositionLocal = reactive({
  x: 0,
  y: 0
});
const panDelta = {
  x: 0,
  y: 0
};

function onPanStart(mouseEvent: MouseEvent) {
  console.log("starting pan");
  state.activeMouseAction = "pan";
  const local = toLocal({
    x: mouseEvent.clientX,
    y: mouseEvent.clientY
  });
  startPanPositionLocal.x = local.x;
  startPanPositionLocal.y = local.y;
  panDelta.x = 0;
  panDelta.y = 0;

  window.addEventListener("pointermove", onPanMove, {
    passive: true
  });
  window.addEventListener("pointerup", onPanEnd);
}

function onPanMove(mouseEvent: MouseEvent & Handleable) {
  if (touchGesturing.value) return; // ignore mouse events while touching

  const currentPanPosition = {
    x: mouseEvent.clientX,
    y: mouseEvent.clientY
  };

  const localCurrentPanPosition = toLocal(currentPanPosition);

  const localDeltaX = localCurrentPanPosition.x - startPanPositionLocal.x;
  const localDeltaY = localCurrentPanPosition.y - startPanPositionLocal.y;

  panDelta.x += localDeltaX;
  panDelta.y += localDeltaY;

  transform.translateX += localDeltaX;
  transform.translateY += localDeltaY;
}

function onPanEnd(_mouseEvent: MouseEvent) {
  window.removeEventListener("pointerup", onPanEnd);
  window.removeEventListener("pointermove", onPanMove);

  state.activeMouseAction = null;

  if (touchGesturing.value) {
    return;
  }

  // save the new position of the net
  emit("update:net-transform", {
    translateX: transform.translateX,
    translateY: transform.translateY
  });

  if (Math.abs(panDelta.x) < 1 && Math.abs(panDelta.y) < 1) {
    // if the pan was less than 1px, we consider it a click
    // and emit a click event on the net.
    deselectAnything();
  }
}

// ===============================
// MOUSE ACTION: SELECTION

function onSelectStart(mouseEvent: MouseEvent) {
  console.log("starting selection", mouseEvent);
  state.activeMouseAction = "select";

  const { x, y } = toLocal({
    x: mouseEvent.clientX,
    y: mouseEvent.clientY
  });

  selectionBox.visible = true;
  selectionBox.x = x;
  selectionBox.y = y;
  selectionBox.width = 0;
  selectionBox.height = 0;

  window.addEventListener("pointermove", onSelectMove, {
    passive: true
  });
  window.addEventListener("pointerup", onSelectEnd);
}

function onSelectMove(mouseEvent: MouseEvent & Handleable) {
  if (touchGesturing.value) return; // ignore mouse events while touching
  if (!selectionBox.visible) {
    return;
  }
  const { x, y } = toLocal({
    x: mouseEvent.clientX,
    y: mouseEvent.clientY
  });
  selectionBox.width = x - selectionBox.x;
  selectionBox.height = y - selectionBox.y;
}

function onSelectEnd(_mouseEvent: MouseEvent) {
  window.removeEventListener("pointerup", onSelectEnd);
  window.removeEventListener("pointermove", onSelectMove);

  if (touchGesturing.value) {
    return;
  }

  console.log("select end");

  const { left, top, width, height } = selectionBoxDimensions.value;
  const nodesInBox = getAllNodesInBox(left, top, width, height);

  if (width < 4 || height < 4) {
    if (nodesInBox.length === 0) {
      deselectAnything();
    }

    const topNode = getTopNode(nodesInBox);
    if (topNode) {
      select({ uid: topNode.uid });
    } else {
      deselectAnything();
    }
  } else {
    const unlockedNodes = nodesInBox.filter((node) => !node?.locked);
    if (unlockedNodes.length > 0) {
      selectMany(unlockedNodes.map((node) => ({
        uid: node.uid
      })));
    } else {
      selectMany(nodesInBox.map((node) => ({
        uid: node.uid
      })));
    }
  }

  state.activeMouseAction = null;

  setTimeout(() => {
    // THIS LINE IS DANGEROUS:
    // it interrupts the click event of the net when in the main function.
    // Leave it in timeout!
    selectionBox.visible = false;
    // I guess that the click is happening on the selectionbox.
    // if it doesn't exist at the end of the pointerup anymore,
    // no click will be registered.
  }, 1);
}

/**
 * Get the top node in the z-order from the given items.
 * The top node is the one that is displayed on top of the others.
 */
function getTopNode<T extends SceneNodeType | "flow" = SceneNodeType>(sizes: (SceneTreeItemSize<T>)[]) {
  return sizes.reduce(
    (prev, current) => {
      const index = [...sceneTree.treeNodes.values()].findIndex(
        (node) => node.uid === current.uid
      );
      if (index > prev.index) {
        return { index, item: current };
      }
      return prev;
    },
    { index: -1, item: null as SceneTreeItemSize<T> | null }
  ).item;
}

// ===============================
// MOUSE ACTION: CREATION

function onCreateStart(mouseEvent: MouseEvent) {
  state.activeMouseAction = "create";

  const { x, y } = toLocal({
    x: mouseEvent.clientX,
    y: mouseEvent.clientY
  });

  creationBox.x = x;
  creationBox.y = y;
  creationBox.width = 0;
  creationBox.height = 0;
  creationBox.placeholderComponent = netTool.value.placeholderComponent;

  window.addEventListener("pointermove", onCreateMove, {
    passive: true
  });
  window.addEventListener("pointerup", onCreateEnd);
}

function onCreateMove(mouseEvent: MouseEvent & Handleable) {
  if (touchGesturing.value) return; // ignore mouse events while touching

  const { x, y } = toLocal({
    x: mouseEvent.clientX,
    y: mouseEvent.clientY
  });
  creationBox.width = x - creationBox.x;
  creationBox.height = y - creationBox.y;
}

function onCreateEnd(mouseEvent: MouseEvent) {
  window.removeEventListener("pointerup", onCreateEnd);
  window.removeEventListener("pointermove", onCreateMove);

  if (touchGesturing.value) {
    return;
  }

  const { left, top, width, height } = creationBoxDimensions.value;

  emit("create", {
    x: left,
    y: top,
    width: width,
    height: height
  });

  creationBox.x = 0;
  creationBox.y = 0;
  creationBox.width = 0;
  creationBox.height = 0;
  creationBox.placeholderComponent = undefined;

  state.activeMouseAction = null;

  console.log("create end");
}

function getAllNodesInBox(x: number, y: number, width: number, height: number) {
  return filter(state.itemSizes, (treeNodeSize): treeNodeSize is SceneTreeItemSize => {
    if (treeNodeSize.superLocked) return false;
    let size: {
      x: number;
      y: number;
      width: number;
      height: number;
      rotation?: number;
    } = state.itemSizes[treeNodeSize.uid];
    if (size == null) size = treeNodeSize;
    return doBoxesOverlap(size, { x, y, width, height });
  });
}

function onItemsMove() {
  updateAutoFlowPositions();

  state.lastElementSizeChange = Date.now();
}

function onItemsResize() {
  updateAutoFlowPositions();

  state.lastElementSizeChange = Date.now();
}

function updateAutoFlowPositions() {
  if (sceneTree.activeScene.flowRelationsDisplay == "hidden") return;
  // Recalculate flow positions
  const flowNetItems = getFlowNetItems(props.flows, state.itemSizes);
  for (const flow of Object.values(flowNetItems)) {
    state.itemSizes[flow.uid] = flowNetItems[flow.uid];
  }
}

function updateSelectedElementSizes() {
  // TODO: Check this.
  const items: {
    uid: Uid<"sceneNode">;
    x: number;
    y: number;
    width: number;
    height: number;
    rotation?: number;
  }[] = [];
  for (const uid of props.selection.selected) {
    const size = state.itemSizes[uid as Uid<"sceneNode">];
    if (!size) continue;
    items.push({
      uid: size.uid as Uid<"sceneNode">,
      x: size.x,
      y: size.y,
      width: size.width,
      height: size.height,
      rotation: size.rotation
    });
  }

  emit("update:element-sizes", items);

  state.lastElementSizeChange = Date.now();
}

// ==============================
// NET ITEM SIZES

// cached size values for size calculations
const containerDimensions = reactive({
  width: 0,
  height: 0,
  scale: 1,
  left: 0,
  right: 0,
  top: 0,
  bottom: 0
});

const canvasDimensions = computed(() => {
  const container = containerDimensions;
  const scale = transform.scale * container.scale;
  const width = Math.round(1000 * scale);
  const height = Math.round(700 * scale);
  const translateX = Math.round(transform.translateX);
  const translateY = Math.round(transform.translateY);
  const left =
    container.left + container.width / 2 - width / 2 + translateX * scale;
  const top =
    container.top + container.height / 2 - height / 2 + translateY * scale;

  const dimensions = {
    scale,
    width,
    height,
    left,
    top,
    x: left,
    y: top,
    right: left + width,
    bottom: top + height,
    translateX,
    translateY,
    containerX: container.left,
    containerY: container.top,
    containerWidth: container.width,
    containerHeight: container.height
  };

  return dimensions;
});

provide(keyCanvasDimensions, canvasDimensions);

watch(canvasDimensions, (val) => {
  emit("update:canvasDimensions", val);
});

const { toLocal } = useNetCoords({
  canvasDimensions: canvasDimensions
});

const mousePos = useMouse();

const localMousePosition = computed(() => {
  return toLocal({
    x: mousePos.x.value,
    y: mousePos.y.value
  });
});

const roundedLocalMousePosition = computed(() => {
  const HALF_GRID_SIZE = GRID_SIZE / 2;
  return {
    x:
      Math.round((localMousePosition.value.x - HALF_GRID_SIZE) / GRID_SIZE) *
      GRID_SIZE,
    y:
      Math.round((localMousePosition.value.y - HALF_GRID_SIZE) / GRID_SIZE) *
      GRID_SIZE
  };
});

const elementNodeAtMouse = computed(() => {
  const nodes = sceneTree.treeNodes.values();
  for (const node of nodes) {
    if (!isElementTreeNode(node)) continue;
    const size = state.itemSizes[node.uid];
    if (size == null) continue;
    if (
      roundedLocalMousePosition.value.x >= size.left &&
      roundedLocalMousePosition.value.x <= size.right &&
      roundedLocalMousePosition.value.y >= size.top &&
      roundedLocalMousePosition.value.y <= size.bottom
    ) {
      return size as SceneTreeItemSize<"element">;
    }
  }
  return null;
});

// ================================
// ELEMENTS

const elementUids = computed((): Set<Uid<string>> => {
  return new Set([
    ...map(props.targets, t => t.uid),
    ...map(props.resources, r => r.uid),
    ...map(props.indices, i => i.uid),
  ]);
});

const sceneFlowNodeStore = useSceneFlowNodeStore();

const allSizesCheckValue = computed(() => {
  return [
    ...sceneTree.treeNodes.values(),
    ...Object.values(props.flows).map(i => ({ uid: i.uid, item: i })),
    ...sceneFlowNodeStore.orderedItems,
    ...Object.values(props.flowBranches).map(i => ({ uid: i.uid, item: i }))
  ].map(
    (i) => (
      i.uid,
      "visible" in i ? i?.visible : null,
      "locked" in i ? i?.locked : null,
      "children" in i ? i.children.map(c => c.uid).join(",") : null,
      "x" in i ? i.x : 0,
      "y" in i ? i.y : 0,
      "width" in i ? i.width : 0,
      "height" in i ? i.height : 0,
      "headlineHidden" in i ? i?.headlineHidden : null, // makes sure minSize changes are detected
      "branchType" in i ? i?.branchType : null,
      "imageId" in i ? i?.imageId : null,
      Date.now()
    )
  );
});

// /**
//  * Update the nodeSizes object when the size of any element changes.
//  */
// watch(
//   [allSizesCheckValue, () => props.impactSourceElement, elementUids],
//   () => {
//     const sizes: Record<"flow" | "index" | "resource" | "target", SceneTreeItemSize> = {};

//     for (const element of [
//       ...Object.values(props.targets),
//       ...Object.values(props.resources),
//       ...Object.values(props.indices)
//     ]) {
//       // Check if the element is visible in the current view
//       if (netLayerItems.visibleItemsInCanvas.has(element) === false) continue;
//       const elementNode = sceneTree.treeNodes.get(element.uid);

//       // // only show resources that should be visible in the net
//       // if (element.visible === false) continue;

//       sizes[element.uid] = {
//         id: element.id,
//         uid: element.uid,
//         type: element.type,
//         x: element.x,
//         y: element.y,
//         width: element.width,
//         height: element.height,
//         top: element.top,
//         right: element.right,
//         left: element.left,
//         bottom: element.bottom,
//         center: {
//           x: element.center.x,
//           y: element.center.y
//         },
//         locked: elementNode?.locked || false,
//         rotation: element.rotation,
//         aspectRatioFixed: false,
//         imageId: element.imageId ?? null,
//         headlineHidden: element.headlineHidden || false
//       };
//     }

//     try {
//       state.itemSizes = sizes;

//       updateAutoFlowPositions();

//       state.lastElementSizeChange = Date.now();
//     } catch (e) {
//       console.error(e);
//     }
//   },
//   {
//     immediate: true
//   }
// );

watch(
  [() => sceneTree.activeScene?.uid, allSizesCheckValue, () => props.impactSourceElement, elementUids, () => [...sceneTree.treeNodes.values()]],
  (data) => {
    const sizes: Record<Uid<"sceneNode">, SceneTreeItemSize> = {};
    for (const [_, treeNode] of sceneTree.treeNodes) {
      if (treeNode.displayPosition != "scene") {
        continue;
      }
      if (isLayerTreeNode(treeNode) || isGroupTreeNode(treeNode)) {
        continue;
      }
      if (!treeNode.visible) {
        continue;
      }
      const node = treeNode.node;

      sizes[node.uid] = {
        uid: node.uid,
        type: node.dataType,

        data: node.data,
        
        x: node.x,
        y: node.y,
        width: node.width,
        height: node.height,
        left: node.x,
        right: node.x + node.width,
        top: node.y,
        bottom: node.y + node.height,
        center: {
          x: Math.round(node.x + node.width / 2),
          y: Math.round(node.y + node.height / 2)
        },
        locked: treeNode.locked ?? false,
        superLocked: treeNode.superLocked ?? false,
        rotation: node.rotation ?? 0,
        gridLocked: node.gridLocked ?? false,

        generated: false,

        aspectRatioLocked: node.aspectRatioLocked ?? false

        // aspectRatioFixed: false,
        // imageId: "imageId" in node.itemProps ? node.itemProps.imageId ?? null : null,
        // headlineHidden: "headlineHidden" in node.itemProps ? node.itemProps.headlineHidden || false : false
      };
    }

    try {
      state.itemSizes = sizes;

      updateAutoFlowPositions();

      state.lastElementSizeChange = Date.now();
    } catch (e) {
      console.error(e);
    }
  },
  { deep: true, immediate: true }
);

/**
 * Update net height and width for css calculations.
 */
function updateDimensionVars() {
  if (!canvasElement.value) {
    console.warn("Net canvas not mounted, could not be measured.");
    return;
  }

  const canvas = canvasElement.value;

  const rect = canvas.getBoundingClientRect();

  containerDimensions.width = Math.round(rect.width);
  containerDimensions.height = Math.round(rect.height);
  containerDimensions.scale =
    Math.min(rect.width, (rect.height * 10) / 7) / 1000;
  containerDimensions.left = rect.left;
  containerDimensions.top = rect.top;
  containerDimensions.right = rect.right;
  containerDimensions.bottom = rect.bottom;

  updateCachedCanvasTransform();
}

const canvasTransform = computed(() => {
  const { scale, translateX, translateY } = canvasDimensions.value;

  return `scale(${scale}) translate3d(${translateX}px, ${translateY}px, 0)`;
});

watch(
  canvasTransform,
  () => {
    updateCachedCanvasTransform();
  },
  { immediate: true }
);

/**
 * We delay updating the canvas for performance:
 * ONLY update if the last frame is done.
 * Otherwise, the net might freeze while we're panning.
 */
function updateCachedCanvasTransform() {
  if (state.isUpdatingCachedCanvasTransform) {
    state.cachedCanvasTransformUpdatePending = true;
    return;
  }
  state.isUpdatingCachedCanvasTransform = true;
  requestAnimationFrame(() => {
    state.cachedCanvasTransformUpdatePending = false;
    state.cachedCanvasTransform = canvasTransform.value;
    state.isUpdatingCachedCanvasTransform = false;
    if (state.cachedCanvasTransformUpdatePending) {
      updateCachedCanvasTransform();
    }
  });
}

// Update transform when props change
watch(
  () => props.netTransform.scale,
  (val) => {
    transform.scale = val;
  },
  { immediate: true }
);
watch(
  () => props.netTransform.translateX,
  (val) => {
    transform.translateX = val;
  },
  { immediate: true }
);
watch(
  () => props.netTransform.translateY,
  (val) => {
    transform.translateY = val;
  },
  { immediate: true }
);

// ==============================
// CREATE ITEMS BY DROPPING

useNetDropHandler(canvasElement, () => props.mode, canvasDimensions);

// ==============================
// FULLSCREEN

function requestFullscreen() {
  const app = document.getElementById("app");
  if (app) {
    if (document.fullscreenElement == app) {
      document.exitFullscreen();
    } else {
      app.requestFullscreen();
    }
  }
}

defineExpose({
  adjustView,
  updateDimensionVars
});
</script>

<style lang="scss">
.net-canvas {
  width: 100%;
  height: 100%;
  background-color: var(--net-color-background);
  background-position: center;
  contain: strict; // should improve rendering performance on chrome
  overflow: hidden;
  user-select: none;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr);
  align-items: center;
  justify-items: center;

  --scene-font-size: v-bind(sceneFontSize);
  --scene-rem: calc(var(--scene-font-size) * 1px);

  font-size: var(--scene-rem);

  &__content {
    width: 0;
    height: 0;
    grid-column: 1 / 2;
    grid-row: 1 / 2;
  }

  &__layer {
    pointer-events: none;
    width: 1000px;
    height: 700px;
    overflow: visible;
    position: absolute;
    top: 50%;
    left: 50%;
    margin-left: -500px;
    margin-top: -350px;
  }
}

.net {
  &__background {
    fill: var(--net-color-background);
  }

  &__container {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    contain: strict;

    --net-color-background: var(--color-background-deeper);

    &.-panning {
      cursor: grabbing;
    }
  }

  &__image {
    position: absolute;
    display: block;
  }

  &__selectionbox {
    position: absolute;
    background-color: var(--color-action-transparent);
    border: 1px solid var(--color-action);
    overflow: hidden;
  }
  &__creationbox {
    position: absolute;
    pointer-events: none;
    background-color: hsla(var(--color-primary-h), 100%, 80%, 30%);
    outline: 3px dashed hsla(var(--color-primary-h), 100%, 80%, 100%);
    outline-offset: -3px;
    border: none;
    border-radius: 0.5rem;
    overflow: hidden;

    &.-transparent {
      background-color: transparent;
      outline-color: transparent;
      border-radius: 0;
      overflow: visible;
    }
  }
}
</style>
